import { useEffect, useRef } from "react";
import { BlurFade } from "@/components/ui/blur-fade";

/* ----------------------------------------------------------------------------
 * The AI Video Workshop's instructor band (Esther, 2026-10-26).
 *
 * Not the shared portrait row: Dave at a laptop inside Monet's real "Irises"
 * (the same museum scan the Code page closes on). He types a prompt, hits
 * enter, and the painting comes alive around him, which is the workshop in one
 * shot. His name sits on a glass card floating over the painting.
 *
 * How it was made: Dave, the table and the laptop were generated from his own
 * portrait on a flat magenta key and composited onto the untouched painting by
 * hand, so the Monet is the real one. Seedance 2.5 animated it with that frame
 * as both first and last frame, so the loop has no seam and never visibly ends.
 *
 * The prompt he types is NOT in the video. It is drawn here, typed out against
 * the video's own clock, so the words stay sharp and can be changed without
 * regenerating anything. PROMPT_TIMING must match the clip: retime it if the
 * video is ever replaced.
 *
 * Layout follows Pika's film panel (Esther, 2026-10-28): his name large over
 * the painting top left, the bio top right, a Webby tag bottom right, the
 * prompt bar bottom left, and the panel opens from 92% to full size as it
 * scrolls into view (desktop, not under reduced motion).
 *
 * The loop is muted and only plays while it is on screen. Reduced motion holds
 * the poster and shows the prompt whole. The instructor entry in
 * shared/workshop-content.ts still feeds the page's JSON-LD; this component
 * only replaces what is drawn.
 * -------------------------------------------------------------------------- */

/* Bump ASSET_V whenever these files are replaced under the same names: they
   are cached for 7 days, so browsers that saw the old files keep showing them
   (2026-10-28, after the loop was swapped out and then restored). */
const ASSET_V = "?v=2";
const POSTER = "/images/ai-video-workshop/dave-monet";
const VIDEO = `/videos/ai-video-workshop/dave-monet.mp4${ASSET_V}`;
const VIDEO_SM = `/videos/ai-video-workshop/dave-monet-960.mp4${ASSET_V}`;

const BIO =
  "A 2026 Webby Award-nominated director with more than 20 years in filmmaking and education, Dave is the Creative Director behind our video advertising. He’s been integrating generative AI into professional video production since 2022.";

const PROMPT = "Bring the garden to life. A soft breeze through the irises, light rippling, slow and natural.";
/** Phones show a slim one-line bar; the full prompt would cover a third of a
 *  ~200px-tall frame (Esther, 2026-10-26). */
const PROMPT_SHORT = "Bring the garden to life.";

/** Seconds into the clip: typing runs from `typeStart` to `typeEnd`, the
 *  enter key lands at `enter`, and the bar fades out at `hide`. */
const PROMPT_TIMING = { typeStart: 0.3, typeEnd: 2.6, enter: 2.9, hide: 6.8 };

export function DaveInstructorSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  /* The prompt follows the video's clock, one frame at a time, with direct DOM
     writes so the section never re-renders while it plays. */
  useEffect(() => {
    const v = videoRef.current;
    const bar = barRef.current;
    const text = textRef.current;
    if (!v || !bar || !text) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      text.textContent = window.matchMedia("(max-width: 767px)").matches ? PROMPT_SHORT : PROMPT;
      bar.dataset.state = "sent";
      return;
    }
    const narrow = window.matchMedia("(max-width: 767px)");
    const { typeStart, typeEnd, enter, hide } = PROMPT_TIMING;
    let raf = 0;
    const tick = () => {
      const t = v.currentTime;
      const prompt = narrow.matches ? PROMPT_SHORT : PROMPT;
      if (t < typeStart || t >= hide) {
        bar.dataset.state = "hidden";
        text.textContent = "";
      } else if (t < enter) {
        const p = Math.min(1, (t - typeStart) / (typeEnd - typeStart));
        text.textContent = prompt.slice(0, Math.round(p * prompt.length));
        bar.dataset.state = "typing";
      } else {
        text.textContent = prompt;
        bar.dataset.state = "sent";
      }
      raf = requestAnimationFrame(tick);
    };
    const start = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(tick); };
    const stop = () => cancelAnimationFrame(raf);
    v.addEventListener("play", start);
    v.addEventListener("pause", stop);
    return () => {
      stop();
      v.removeEventListener("play", start);
      v.removeEventListener("pause", stop);
    };
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) {
      v.poster = `${POSTER}-1600.webp${ASSET_V}`;
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) v.play().catch(() => {});
      return;
    }
    /* Two observers: the poster is set when the band is ~1000px away (a video
       poster is not lazy, so it would otherwise download on page load, far
       above this band), and playback follows actual visibility. */
    const near = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        v.poster = `${POSTER}-${window.innerWidth < 768 ? 800 : 1600}.webp${ASSET_V}`;
        near.disconnect();
      },
      { rootMargin: "1000px 0px" },
    );
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.2 },
    );
    near.observe(v);
    io.observe(v);
    return () => { near.disconnect(); io.disconnect(); };
  }, []);

  /* The panel opens up as it arrives: 0.92 when its top is a full screen
     below the fold, 1 once it reaches the upper third. */
  const stageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = stageRef.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const set = () => {
      raf = 0;
      if (window.innerWidth < 768) { el.style.removeProperty("--avw-s"); return; }
      const top = el.getBoundingClientRect().top;
      const p = Math.min(Math.max((window.innerHeight - top) / (window.innerHeight * 0.75), 0), 1);
      el.style.setProperty("--avw-s", (0.92 + 0.08 * p).toFixed(4));
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(set); };
    set();
    window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => { window.removeEventListener("scroll", on); window.removeEventListener("resize", on); if (raf) cancelAnimationFrame(raf); };
  }, []);

  return (
    <section id="instructor" aria-labelledby="instructor-heading" className="avw-dave">
      <style>{`
        .avw-dave { padding: clamp(110px, 12vw, 180px) 0 clamp(90px, 10vw, 160px); }
        .avw-dave-h {
          font-family: var(--font-display);
          font-weight: 400;
          font-size: clamp(36px, 4.6vw, 62px);
          line-height: 1.12;
          letter-spacing: -0.03em;
          text-align: center;
          max-width: 18ch;
          margin: 0 auto;
          text-wrap: balance;
        }
        /* Breakout width, centred: wider than the content column, never edge
           to edge (client/CLAUDE.md). */
        .avw-dave-stage {
          position: relative;
          width: min(1428px, calc(100vw - 96px));
          margin: clamp(48px, 5vw, 72px) 0 0 50%;
          transform: translateX(-50%) scale(var(--avw-s, 1));
          transform-origin: 50% 0;
          will-change: transform;
          aspect-ratio: 16 / 9;
          border-radius: 10px;
          overflow: hidden;
          background: #E9E4D8;
        }
        .avw-dave-media { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; }
        /* The prompt bar, over the painting below the laptop. */
        .avw-prompt {
          position: absolute;
          left: clamp(16px, 3vw, 48px);
          bottom: clamp(16px, 3vw, 48px);
          width: min(560px, 52%);
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 16px 18px;
          border-radius: 10px;
          background: rgba(22, 23, 26, 0.78);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.14);
          color: #fff;
          font-family: var(--font-mono);
          font-size: clamp(12px, 1.05vw, 15px);
          line-height: 1.5;
          transition: opacity 400ms ease, transform 400ms ease;
        }
        .avw-prompt[data-state="hidden"] { opacity: 0; transform: translateY(8px); }
        .avw-prompt-k { flex: none; color: var(--pg-chrome-ink, #F3BDAF); letter-spacing: 0.12em; }
        .avw-prompt-t { flex: 1; min-height: 1.5em; }
        .avw-prompt[data-state="typing"] .avw-prompt-t::after {
          content: "";
          display: inline-block;
          width: 0.55em;
          height: 1.1em;
          margin-left: 2px;
          vertical-align: -0.18em;
          background: currentColor;
          animation: avw-caret 0.9s steps(1) infinite;
        }
        @keyframes avw-caret { 50% { opacity: 0; } }
        .avw-prompt-enter {
          flex: none;
          align-self: flex-end;
          padding: 3px 8px;
          border-radius: 5px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          font-size: 0.85em;
          opacity: 0.55;
          transition: background 200ms ease, color 200ms ease, opacity 200ms ease;
        }
        .avw-prompt[data-state="sent"] .avw-prompt-enter {
          opacity: 1;
          background: var(--pg-chrome-ink, #F3BDAF);
          color: #1D1D1F;
          border-color: transparent;
        }
        /* Pika's film panel: type sits on the painting itself, over a soft
           scrim at the top so white reads on the bright garden. */
        .avw-dave-scrim {
          position: absolute; inset: 0; pointer-events: none;
          background:
            linear-gradient(180deg, rgba(12, 16, 12, 0.58) 0%, rgba(12, 16, 12, 0.18) 32%, rgba(12, 16, 12, 0) 50%),
            linear-gradient(0deg, rgba(12, 16, 12, 0.38) 0%, rgba(12, 16, 12, 0) 26%);
        }
        .avw-dave-over {
          position: absolute;
          left: clamp(24px, 3vw, 48px);
          right: clamp(24px, 3vw, 48px);
          top: clamp(22px, 2.8vw, 44px);
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 40px;
          color: #F5F5F7;
          pointer-events: none;
        }
        .avw-dave-name {
          font-family: var(--font-display);
          font-weight: 400;
          font-size: clamp(56px, 7.2vw, 112px);
          line-height: 0.92;
          letter-spacing: -0.05em;
          margin: 0;
        }
        .avw-dave-bio { width: min(380px, 32%); }
        .avw-dave-role {
          margin: 0;
          font-family: var(--font-sans);
          font-size: 15px;
          font-weight: 500;
          color: var(--pg-chrome-ink, #F3BDAF);
        }
        .avw-dave-cred {
          margin: 12px 0 0;
          font-family: var(--font-sans);
          font-size: 16px;
          line-height: 1.55;
          color: rgba(245, 245, 247, 0.92);
        }
        .avw-dave-tag {
          position: absolute;
          right: clamp(16px, 3vw, 48px);
          bottom: clamp(16px, 3vw, 48px);
          padding: 10px 16px;
          border-radius: 999px;
          background: rgba(20, 20, 20, 0.42);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          color: #F5F5F7;
          font-family: var(--font-sans);
          font-size: 14px;
          font-weight: 500;
        }
        @media (max-width: 767px) {
          .avw-prompt { left: 8px; right: auto; bottom: 8px; width: auto; max-width: calc(100% - 16px); align-items: center; padding: 5px 8px; gap: 8px; border-radius: 7px; font-size: 10px; line-height: 1.3; white-space: nowrap; }
          .avw-prompt-k { letter-spacing: 0.08em; }
          .avw-prompt-enter { align-self: center; padding: 1px 5px; border-radius: 4px; }
        }
        @media (max-width: 1023px) {
          .avw-dave-bio { width: min(320px, 36%); }
          .avw-dave-cred { font-size: 14px; }
        }
        /* Phones: the name stays on the frame; the bio drops just under it,
           clear of the prompt bar along the frame's foot. */
        @media (max-width: 767px) {
          .avw-dave-stage { width: calc(100vw - 40px); overflow: visible; aspect-ratio: auto; background: none; }
          .avw-dave-frame { position: relative; aspect-ratio: 16 / 9; border-radius: 10px; overflow: hidden; }
          .avw-dave-name { font-size: 38px; }
          .avw-dave-tag { display: none; }
          .avw-dave-bio-m { margin: 16px 0 0; }
          .avw-dave-bio-m .avw-dave-role { color: var(--pg-chrome, #9B0032); }
          .avw-dave-bio-m .avw-dave-cred { color: rgba(29, 29, 31, 0.72); font-size: 15px; }
        }
        @media (min-width: 768px) { .avw-dave-bio-m { display: none; } }
        @media (max-width: 767px) { .avw-dave-over .avw-dave-bio { display: none; } }
        @media (min-width: 768px) {
          .avw-dave-frame { position: absolute; inset: 0; }
        }
      `}</style>

      <BlurFade inView delay={0} duration={0.5} yOffset={12}>
        <h2 id="instructor-heading" className="avw-dave-h">
          Learn from a director who makes AI commercials for a living.
        </h2>
      </BlurFade>

      <div className="avw-dave-stage" ref={stageRef}>
        <div className="avw-dave-frame">
          <video
            ref={videoRef}
            className="avw-dave-media"
            muted
            loop
            playsInline
            preload="none"
            aria-label="Dave Spector types a prompt on a laptop and Claude Monet's painting Irises comes alive behind him"
          >
            {/* Phones take the 960w encode (588KB against 1.4MB). */}
            <source src={VIDEO_SM} type="video/mp4" media="(max-width: 767px)" />
            <source src={VIDEO} type="video/mp4" />
          </video>
          <div className="avw-dave-scrim" aria-hidden="true" />
          <div className="avw-dave-over">
            <p className="avw-dave-name">Dave<br />Spector</p>
            <div className="avw-dave-bio">
              <p className="avw-dave-role">Creative Director, AdVenture Media</p>
              <p className="avw-dave-cred">{BIO}</p>
            </div>
          </div>
          <span className="avw-dave-tag" aria-hidden="true">Webby nominee · 2026</span>
          <div ref={barRef} className="avw-prompt" data-state="hidden" aria-hidden="true">
            <span className="avw-prompt-k">PROMPT</span>
            <span ref={textRef} className="avw-prompt-t" />
            <span className="avw-prompt-enter">↵</span>
          </div>
        </div>
        <div className="avw-dave-bio-m">
          <p className="avw-dave-role">Creative Director, AdVenture Media</p>
          <p className="avw-dave-cred">{BIO}</p>
        </div>
      </div>
    </section>
  );
}
